// V3 Command Center Calendar — unified schedule across every dated
// surface in the app (tasks, BuildTracker milestones, deal deadlines,
// closings, manual events).
//
// Views:
//   • Month grid (default)   — traditional calendar with event chips
//   • Agenda list            — chronological upcoming-events feed
//
// Interactions:
//   • Click empty day → "Quick Add Event" dialog
//   • Click event chip → jumps to source (property workspace or task view)
//   • Toggle layer visibility with pill chips at top
//   • Prev / Next / Today month navigation

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths,
  parseISO, isToday, addDays, differenceInCalendarDays,
} from "date-fns";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, ListChecks,
  Grid3x3, Loader2, X, MapPin, Link as LinkIcon,
} from "lucide-react";

// Layer palette — must mirror backend LAYER_STYLE for visual consistency.
const LAYERS = [
  { key: "task",      label: "Tasks",        color: "#0EA5E9" },
  { key: "build",     label: "BuildTracker", color: "#F59E0B" },
  { key: "deal",      label: "Deal Dates",   color: "#10B981" },
  { key: "closing",   label: "Closings",     color: "#059669" },
  { key: "jarvis",    label: "Jarvis",       color: "#A855F7" },
  { key: "deadline",  label: "Deadlines",    color: "#DC2626" },
  { key: "marketing", label: "Marketing",    color: "#EF4444" },
  { key: "personal",  label: "Personal",     color: "#64748B" },
];

export default function CalendarPage() {
  const [cursor,   setCursor]   = useState(new Date());
  const [view,     setView]     = useState("month");    // month | agenda
  const [events,   setEvents]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [visible,  setVisible]  = useState(new Set(LAYERS.map((l) => l.key)));
  const [showAdd,  setShowAdd]  = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const nav = useNavigate();

  const rangeStart = useMemo(() => startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 }), [cursor]);
  const rangeEnd   = useMemo(() => endOfWeek(endOfMonth(cursor),   { weekStartsOn: 0 }), [cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/v3/calendar/events", {
        params: {
          start:  rangeStart.toISOString(),
          end:    rangeEnd.toISOString(),
          layers: Array.from(visible).join(","),
        },
      });
      setEvents(res.data?.events || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd, visible]);

  useEffect(() => { load(); }, [load]);

  const toggleLayer = (key) => {
    setVisible((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const eventsByDay = useMemo(() => {
    const map = new Map();
    events.forEach((e) => {
      const iso = (e.starts_at || "").slice(0, 10);
      if (!iso) return;
      const key = iso;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return map;
  }, [events]);

  const openEventSource = (e) => {
    if (e.source === "task" && e.property_id) {
      nav(`/v3/properties/${e.property_id}`);
    } else if (e.property_id) {
      nav(`/v3/properties/${e.property_id}`);
    }
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setShowAdd(true);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="calendar-page">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* ── Header ────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-widest text-amber-700">
              <CalendarDays className="w-3.5 h-3.5" /> Command Center · Calendar
            </div>
            <h1 className="mt-1 text-[26px] sm:text-[32px] font-bold text-slate-900 leading-tight">
              {format(cursor, "MMMM yyyy")}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
              <button data-testid="cal-view-month" onClick={() => setView("month")}
                      className={"px-3 py-2 text-[12.5px] font-semibold flex items-center gap-1.5 " +
                        (view === "month" ? "bg-amber-100 text-amber-900" : "text-slate-600 hover:bg-slate-50")}>
                <Grid3x3 className="w-3.5 h-3.5" /> Month
              </button>
              <button data-testid="cal-view-agenda" onClick={() => setView("agenda")}
                      className={"px-3 py-2 text-[12.5px] font-semibold flex items-center gap-1.5 border-l border-slate-200 " +
                        (view === "agenda" ? "bg-amber-100 text-amber-900" : "text-slate-600 hover:bg-slate-50")}>
                <ListChecks className="w-3.5 h-3.5" /> Agenda
              </button>
            </div>
            <button data-testid="cal-prev" onClick={() => setCursor((c) => subMonths(c, 1))}
                    className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button data-testid="cal-today" onClick={() => setCursor(new Date())}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50">
              Today
            </button>
            <button data-testid="cal-next" onClick={() => setCursor((c) => addMonths(c, 1))}
                    className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button data-testid="cal-add" onClick={() => { setSelectedDay(new Date()); setShowAdd(true); }}
                    className="inline-flex items-center gap-1.5 rounded-lg text-white text-[12.5px] font-semibold px-3.5 py-2 shadow-md"
                    style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
              <Plus className="w-3.5 h-3.5" /> Add Event
            </button>
          </div>
        </div>

        {/* ── Layer toggles ─────────────────────────────── */}
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="cal-layers">
          {LAYERS.map((l) => {
            const active = visible.has(l.key);
            return (
              <button key={l.key} type="button" onClick={() => toggleLayer(l.key)}
                      data-testid={`cal-layer-${l.key}`}
                      className={"inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11.5px] font-semibold transition-all " +
                        (active ? "border-transparent bg-white shadow-sm text-slate-800" : "border-slate-200 bg-slate-100 text-slate-400 line-through")}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color, opacity: active ? 1 : 0.4 }} />
                {l.label}
              </button>
            );
          })}
          {loading && (
            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-500 ml-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </span>
          )}
        </div>

        {/* ── Views ──────────────────────────────────────── */}
        {view === "month" ? (
          <MonthGrid
            cursor={cursor}
            rangeStart={rangeStart} rangeEnd={rangeEnd}
            eventsByDay={eventsByDay}
            onDayClick={handleDayClick}
            onEventClick={openEventSource}
          />
        ) : (
          <AgendaList
            events={events}
            onEventClick={openEventSource}
          />
        )}
      </div>

      {showAdd && (
        <AddEventDialog
          initialDate={selectedDay || new Date()}
          onClose={() => setShowAdd(false)}
          onSaved={(created) => {
            setShowAdd(false);
            setEvents((prev) => [...prev, {
              id: created.id, source: "manual", source_id: created.id,
              title: created.title, description: created.description,
              starts_at: created.starts_at, ends_at: created.ends_at,
              all_day: created.all_day, layer: created.layer,
              color: created.color, location: created.location, url: created.url,
              property_id: created.property_id, editable: true,
            }]);
            toast.success(`Event added: ${created.title}`);
          }}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// MONTH GRID
// ═════════════════════════════════════════════════════════════════════════

function MonthGrid({ cursor, rangeStart, rangeEnd, eventsByDay, onDayClick, onEventClick }) {
  const days = eachDayOfInterval({ start: rangeStart, end: rangeEnd });
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {weekdays.map((w) => (
          <div key={w} className="px-2 py-2 text-[10.5px] font-bold uppercase tracking-widest text-slate-500 text-center">{w}</div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 auto-rows-fr" data-testid="cal-month-grid">
        {days.map((day) => {
          const isCurMonth = isSameMonth(day, cursor);
          const isTodayCell = isToday(day);
          const dayKey = format(day, "yyyy-MM-dd");
          const dayEvents = eventsByDay.get(dayKey) || [];
          return (
            <button
              key={dayKey}
              type="button"
              onClick={() => onDayClick(day)}
              data-testid={`cal-day-${dayKey}`}
              className={
                "relative border-r border-b border-slate-100 p-1.5 min-h-[100px] text-left transition-colors " +
                (isCurMonth ? "bg-white hover:bg-amber-50/50" : "bg-slate-50/70 hover:bg-slate-100/70")
              }
            >
              <div className={"text-[11.5px] font-bold mb-1 " +
                (isTodayCell
                  ? "inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-600 text-white"
                  : isCurMonth ? "text-slate-800" : "text-slate-400")}>
                {format(day, "d")}
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((e) => (
                  <div
                    key={e.id}
                    onClick={(ev) => { ev.stopPropagation(); onEventClick(e); }}
                    data-testid={`cal-event-${e.id}`}
                    className="rounded px-1.5 py-0.5 text-[10.5px] font-semibold text-white truncate cursor-pointer hover:brightness-110"
                    style={{ backgroundColor: e.color || "#64748B" }}
                    title={e.title}
                  >
                    {e.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[10px] font-semibold text-amber-700 pl-1">
                    +{dayEvents.length - 3} more
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// AGENDA LIST
// ═════════════════════════════════════════════════════════════════════════

function AgendaList({ events, onEventClick }) {
  if (!events.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
        No events in this range. Try widening the date window or toggling more layers on.
      </div>
    );
  }
  const grouped = new Map();
  events.forEach((e) => {
    const iso = (e.starts_at || "").slice(0, 10);
    if (!iso) return;
    if (!grouped.has(iso)) grouped.set(iso, []);
    grouped.get(iso).push(e);
  });
  const sortedDates = Array.from(grouped.keys()).sort();
  const today = format(new Date(), "yyyy-MM-dd");

  return (
    <div className="space-y-3" data-testid="cal-agenda">
      {sortedDates.map((iso) => {
        const day = parseISO(iso);
        const list = grouped.get(iso);
        const relDays = differenceInCalendarDays(day, new Date());
        const relLabel = relDays === 0 ? "TODAY" : relDays === 1 ? "TOMORROW" : relDays > 0 ? `IN ${relDays} DAYS` : `${Math.abs(relDays)}d AGO`;
        return (
          <div key={iso} className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10.5px] font-bold uppercase tracking-widest text-amber-700">
                  {format(day, "EEE, MMM d, yyyy")}
                </div>
                <div className={"text-[11px] font-bold " + (iso === today ? "text-amber-700" : "text-slate-500")}>
                  {relLabel} · {list.length} event{list.length !== 1 ? "s" : ""}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              {list.map((e) => (
                <div
                  key={e.id}
                  onClick={() => onEventClick(e)}
                  data-testid={`cal-agenda-item-${e.id}`}
                  className="rounded-lg border border-slate-200 p-2.5 flex items-start gap-2.5 cursor-pointer hover:border-amber-300 hover:bg-amber-50/40"
                >
                  <span className="w-1.5 h-full rounded-full mt-0.5 shrink-0" style={{ backgroundColor: e.color || "#64748B", minHeight: "24px" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold text-slate-900 truncate">{e.title}</div>
                    <div className="text-[10.5px] text-slate-500 flex items-center gap-2 mt-0.5">
                      <span className="uppercase tracking-widest font-bold" style={{ color: e.color || "#64748B" }}>{e.layer}</span>
                      {e.location && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {e.location}</span>}
                      {!e.all_day && e.starts_at && <span>· {format(parseISO(e.starts_at), "h:mm a")}</span>}
                    </div>
                    {e.description && <div className="text-[11.5px] text-slate-600 mt-1 line-clamp-2">{e.description}</div>}
                  </div>
                  {e.url && (
                    <a href={e.url} target="_blank" rel="noreferrer"
                       onClick={(ev) => ev.stopPropagation()}
                       className="text-slate-400 hover:text-amber-600">
                      <LinkIcon className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ADD EVENT DIALOG
// ═════════════════════════════════════════════════════════════════════════

function AddEventDialog({ initialDate, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "",
    date:  format(initialDate, "yyyy-MM-dd"),
    time:  format(new Date(), "HH:mm"),
    all_day: true,
    layer: "personal",
    location: "",
    description: "",
    url: "",
    // Phase 2 · SMS reminders
    reminder_1d: false,
    reminder_1h: false,
    notify_operator: true,
    contact_name: "",
    contact_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      const starts_at = form.all_day
        ? new Date(`${form.date}T00:00:00`).toISOString()
        : new Date(`${form.date}T${form.time}:00`).toISOString();
      const offsets = [];
      if (form.reminder_1d) offsets.push(24 * 60);
      if (form.reminder_1h) offsets.push(60);
      const notify_contacts = form.contact_phone.trim()
        ? [{ name: form.contact_name.trim() || null, phone: form.contact_phone.trim() }]
        : [];
      const res = await api.post("/v3/calendar/events", {
        title: form.title.trim(),
        starts_at,
        all_day: form.all_day,
        layer: form.layer,
        location: form.location.trim() || undefined,
        description: form.description.trim() || undefined,
        url: form.url.trim() || undefined,
        reminder_offsets_minutes: offsets.length ? offsets : undefined,
        notify_operator: form.notify_operator,
        notify_contacts,
      });
      onSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save event");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center px-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            data-testid="cal-add-dialog"
            className="w-full max-w-md rounded-2xl bg-white border border-slate-200 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-widest text-amber-700">Add Event</div>
            <h2 className="text-[18px] font-bold text-slate-900">Quick add</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">Title *</label>
            <input required autoFocus value={form.title} onChange={set("title")} data-testid="cal-add-title"
                   placeholder="e.g. Middleton Seminar VIP" maxLength={200}
                   className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">Date</label>
              <input type="date" value={form.date} onChange={set("date")} data-testid="cal-add-date"
                     className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px]" />
            </div>
            {!form.all_day && (
              <div>
                <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">Time</label>
                <input type="time" value={form.time} onChange={set("time")} data-testid="cal-add-time"
                       className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px]" />
              </div>
            )}
            {form.all_day && (
              <label className="flex items-center gap-2 mt-6 text-[12.5px] text-slate-700">
                <input type="checkbox" checked={form.all_day}
                       onChange={(e) => setForm((f) => ({ ...f, all_day: e.target.checked }))}
                       data-testid="cal-add-allday" />
                All-day event
              </label>
            )}
            {!form.all_day && (
              <label className="flex items-center gap-2 col-span-2 text-[12.5px] text-slate-700 -mt-1">
                <input type="checkbox" checked={form.all_day}
                       onChange={(e) => setForm((f) => ({ ...f, all_day: e.target.checked }))}
                       data-testid="cal-add-allday" />
                All-day event
              </label>
            )}
          </div>
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">Layer</label>
            <select value={form.layer} onChange={set("layer")} data-testid="cal-add-layer"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] bg-white">
              {LAYERS.filter((l) => !["task", "build", "deal", "closing"].includes(l.key)).map((l) => (
                <option key={l.key} value={l.key}>{l.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">Location (optional)</label>
            <input value={form.location} onChange={set("location")} data-testid="cal-add-location"
                   className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px]" />
          </div>
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500">Notes (optional)</label>
            <textarea value={form.description} onChange={set("description")}
                      data-testid="cal-add-description" rows={2}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px]" />
          </div>

          {/* Phase 2 · SMS reminders */}
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2"
               data-testid="cal-add-reminders">
            <div className="text-[10.5px] font-bold uppercase tracking-widest text-amber-700">
              SMS reminders (via your Twilio)
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-slate-700">
              <label className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={form.reminder_1d}
                       onChange={(e) => setForm((f) => ({ ...f, reminder_1d: e.target.checked }))}
                       data-testid="cal-add-reminder-1d" />
                1 day before
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={form.reminder_1h}
                       onChange={(e) => setForm((f) => ({ ...f, reminder_1h: e.target.checked }))}
                       data-testid="cal-add-reminder-1h" />
                1 hour before
              </label>
            </div>
            {(form.reminder_1d || form.reminder_1h) && (
              <>
                <label className="flex items-center gap-2 text-[12px] text-slate-700 mt-1">
                  <input type="checkbox" checked={form.notify_operator}
                         onChange={(e) => setForm((f) => ({ ...f, notify_operator: e.target.checked }))}
                         data-testid="cal-add-notify-operator" />
                  Text me
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input value={form.contact_name} onChange={set("contact_name")}
                         data-testid="cal-add-contact-name"
                         placeholder="Also text (name)"
                         className="rounded-md border border-slate-200 px-2.5 py-1.5 text-[12.5px]" />
                  <input value={form.contact_phone} onChange={set("contact_phone")}
                         data-testid="cal-add-contact-phone"
                         placeholder="+15551234567"
                         className="rounded-md border border-slate-200 px-2.5 py-1.5 text-[12.5px]" />
                </div>
                <div className="text-[10.5px] text-slate-500 leading-snug">
                  Contact reminders include a STOP-to-opt-out footer. Uses your BYO Twilio.
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="text-[13px] text-slate-500 hover:text-slate-800 px-3 py-2">Cancel</button>
          <button type="submit" disabled={busy || !form.title.trim()}
                  data-testid="cal-add-submit"
                  className="rounded-lg text-white text-[13px] font-semibold px-4 py-2 shadow-md disabled:opacity-60"
                  style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
            {busy ? "Adding…" : "Add Event"}
          </button>
        </div>
      </form>
    </div>
  );
}
